import { useCallback, useEffect, useMemo, useState } from "react";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "./Alert";
import {
  canUseFirebaseMessaging,
  isFirebaseMessagingConfigured,
  requestAndSaveWebFcmToken,
  subscribeToForegroundMessages,
} from "../services/firebaseMessaging";
import "./webNotificationManager.css";

const PROMPT_DISMISSED_KEY = "iot-web-notification-prompt-dismissed";

const getPayloadTitle = (payload) =>
  payload?.notification?.title ||
  payload?.data?.title ||
  payload?.data?.name ||
  "New notification";

const getPayloadMessage = (payload) =>
  payload?.notification?.body ||
  payload?.data?.body ||
  payload?.data?.message ||
  "Open notifications to view the latest update.";

const getFriendlyFirebaseError = (error) => {
  const message = String(error?.message || "");

  if (message.includes("API key not valid") || message.includes("installations/request-failed")) {
    return "Firebase API key is not valid for this web app. Check REACT_APP_FIREBASE_API_KEY, restart the frontend, and make sure the key is allowed for localhost/this domain.";
  }

  if (
    message.includes("missing required authentication credential") ||
    message.includes("messaging/token-subscribe-failed")
  ) {
    return "Firebase could not register this browser for FCM. Enable the FCM Registration API for the sama-iot Google Cloud project, then check API key restrictions include localhost and fcmregistrations.googleapis.com.";
  }

  if (message.includes("messaging/permission-blocked")) {
    return "Notifications are blocked in this browser. Enable them from browser site settings.";
  }

  if (message.includes("messaging/unsupported-browser")) {
    return "This browser does not support Firebase browser notifications.";
  }

  return message || "Could not enable browser notifications right now.";
};

const WebNotificationManager = ({ authed, user }) => {
  const [canPrompt, setCanPrompt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    type: "info",
    message: "",
    position: "right",
  });

  const userId = user?.id;
  const configured = useMemo(() => isFirebaseMessagingConfigured(), []);

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: "right",
    });
  }, []);

  const syncToken = useCallback(
    async (forcePermissionPrompt = false) => {
      if (!authed || !userId || !configured) return;

      setSaving(true);
      try {
        const result = await requestAndSaveWebFcmToken({ userId, forcePermissionPrompt });

        if (result.status === "ready") {
          localStorage.removeItem(PROMPT_DISMISSED_KEY);
          setCanPrompt(false);
          if (forcePermissionPrompt) {
            showAlert("success", "Browser notifications are enabled.");
          }
        } else if (result.status === "needs-permission") {
          const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY) === "1";
          setCanPrompt(!dismissed);
        } else if (result.status === "denied") {
          setCanPrompt(false);
          if (forcePermissionPrompt) {
            showAlert(
              "warning",
              "Notifications are blocked in this browser. Enable them from browser site settings."
            );
          }
        }
      } catch (error) {
        console.error("[FCM] Failed to register web notification token:", error);
        if (forcePermissionPrompt) {
          showAlert("error", getFriendlyFirebaseError(error));
        }
      } finally {
        setSaving(false);
      }
    },
    [authed, configured, showAlert, userId]
  );

  useEffect(() => {
    let mounted = true;

    const evaluateSupport = async () => {
      if (!authed || !userId || !configured) {
        setCanPrompt(false);
        return;
      }

      const supported = await canUseFirebaseMessaging();
      if (!mounted || !supported) return;

      if (Notification.permission === "granted") {
        await syncToken(false);
      } else if (Notification.permission === "default") {
        setCanPrompt(localStorage.getItem(PROMPT_DISMISSED_KEY) !== "1");
      } else {
        setCanPrompt(false);
      }
    };

    evaluateSupport();

    return () => {
      mounted = false;
    };
  }, [authed, configured, syncToken, userId]);

  useEffect(() => {
    if (!authed || !configured) return undefined;

    let unsubscribe = () => {};
    let active = true;

    subscribeToForegroundMessages((payload) => {
      if (!active) return;
      showAlert("info", `${getPayloadTitle(payload)}\n${getPayloadMessage(payload)}`);
    }).then((nextUnsubscribe) => {
      if (!active) {
        nextUnsubscribe();
        return;
      }
      unsubscribe = nextUnsubscribe;
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [authed, configured, showAlert]);

  const dismissPrompt = () => {
    localStorage.setItem(PROMPT_DISMISSED_KEY, "1");
    setCanPrompt(false);
  };

  return (
    <>
      <Alert
        open={alertState.open}
        close={() => setAlertState((prev) => ({ ...prev, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      {canPrompt ? (
        <div className="web-notification-prompt" role="status">
          <div className="web-notification-prompt-icon" aria-hidden="true">
            <NotificationsActiveOutlinedIcon fontSize="small" />
          </div>
          <div className="web-notification-prompt-copy">
            <strong>Enable browser notifications</strong>
            <span>Get device and automation alerts even when this tab is not open.</span>
          </div>
          <button
            type="button"
            className="web-notification-prompt-primary"
            onClick={() => syncToken(true)}
            disabled={saving}
          >
            {saving ? "Enabling..." : "Enable"}
          </button>
          <button
            type="button"
            className="web-notification-prompt-close"
            onClick={dismissPrompt}
            aria-label="Dismiss notification prompt"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      ) : null}
    </>
  );
};

export default WebNotificationManager;
